import React, { useMemo, useState } from "react";
import { Button } from "@mui/material";
import {
  getAdminParticipants,
  getAdminQuestionnaires,
  getAdminSummary,
  getAdminActivity,
} from "../api/questionnaire";
import {
  CONDITIONS,
  conditionLabel,
  buildConditionSummary,
  buildInteractionSummary,
  buildDataQuality,
  downloadJson,
  downloadCsv,
} from "../utils/adminAnalytics";

const C = {
  ink: "#0f172a",
  sub: "#475569",
  muted: "#64748b",
  line: "#dbe5f0",
  line2: "#cbd5e1",
  panel: "#ffffff",
  bg: "#f8fafc",
  blue: "#2563eb",
  blueBg: "#eff6ff",
  blueInk: "#1e3a8a",
  green: "#16a34a",
  amber: "#d97706",
  red: "#b91c1c",
};

const TARGET_TYPES = ["caption_token", "image_region", "evidence_card", "button"];
const TARGET_LABEL = {
  caption_token: "Caption words",
  image_region: "Image regions",
  evidence_card: "Evidence cards",
  button: "Buttons",
};

function nowStamp() {
  // Filenames only — browser runtime, safe to use the real clock.
  return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}

// A real Prolific participant code is a 24-char lowercase-hex Prolific PID.
// Pilots/tests (PILOT_*, TEST*, DEBUG*, MIBA, 11, …) never match.
const PROLIFIC_CODE_RE = /^[0-9a-f]{24}$/;
function isProlificCode(code) {
  return PROLIFIC_CODE_RE.test(String(code || ""));
}

function fmt(value, suffix = "") {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}${suffix}`;
}

function ms(msValue) {
  const n = Number(msValue);
  if (!Number.isFinite(n) || n <= 0) return "0s";
  if (n < 1000) return `${n} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`;
  const m = Math.floor(n / 60000);
  const s = Math.round((n % 60000) / 1000);
  return `${m}m ${s}s`;
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div
      style={{
        flex: "1 1 160px",
        background: C.panel,
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 18px",
      }}
    >
      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: C.muted, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, color: accent || C.ink, marginTop: 6, lineHeight: 1.1 }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>{sub}</div> : null}
    </div>
  );
}

// Horizontal bar with a numeric label; `value` normalized against `max`.
function Bar({ value, max, color, text }) {
  const pct = value === null || value === undefined || !max ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
      <div style={{ position: "relative", flex: 1, height: 8, background: "#eef2f7", borderRadius: 6 }}>
        <div style={{ position: "absolute", inset: 0, width: `${pct}%`, background: color || C.blue, borderRadius: 6 }} />
      </div>
      <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, color: C.ink, minWidth: 46, textAlign: "right" }}>
        {text}
      </span>
    </div>
  );
}

function SectionCard({ title, subtitle, right, children }) {
  return (
    <section style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 16, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: C.ink, fontSize: 18 }}>{title}</h2>
          {subtitle ? <p style={{ margin: "6px 0 0", color: C.muted, fontSize: 13 }}>{subtitle}</p> : null}
        </div>
        {right}
      </div>
      <div style={{ marginTop: 16 }}>{children}</div>
    </section>
  );
}

function ExportButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 12px",
        borderRadius: 10,
        border: `1px solid ${C.line2}`,
        background: "#fff",
        color: C.blueInk,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      ⬇ {children}
    </button>
  );
}

function th(extra) {
  return {
    textAlign: "left",
    padding: "9px 12px",
    background: C.blueBg,
    borderBottom: `1px solid ${C.line2}`,
    color: C.blueInk,
    fontSize: 12,
    whiteSpace: "nowrap",
    ...extra,
  };
}
function td(extra) {
  return {
    padding: "9px 12px",
    borderBottom: "1px solid #e2e8f0",
    color: "#1e293b",
    fontSize: 13,
    verticalAlign: "top",
    ...extra,
  };
}

function DataTable({ title, rows, maxHeight = 360 }) {
  const columns = useMemo(() => {
    if (!rows?.length) return [];
    return Array.from(
      rows.reduce((set, row) => {
        Object.keys(row || {}).forEach((key) => set.add(key));
        return set;
      }, new Set())
    );
  }, [rows]);

  return (
    <section style={{ marginBottom: 22 }}>
      <h3 style={{ marginBottom: 10, color: C.ink, fontSize: 15 }}>
        {title} <span style={{ color: C.muted, fontWeight: 400 }}>({rows?.length || 0})</span>
      </h3>
      {!rows?.length ? (
        <p style={{ margin: 0, color: C.muted }}>No records found.</p>
      ) : (
        <div style={{ overflow: "auto", maxHeight, border: `1px solid ${C.line2}`, borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
            <thead style={{ position: "sticky", top: 0 }}>
              <tr>
                {columns.map((column) => (
                  <th key={column} style={th()}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`${title}-${rowIndex}`}>
                  {columns.map((column) => (
                    <td key={column} style={td()}>
                      {String(row?.[column] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Badge({ children, color }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: `${color}1a`,
        color,
        marginRight: 4,
      }}
    >
      {children}
    </span>
  );
}

// ---------- Overview tab ----------

function OverviewTab({ summary }) {
  const conditionRows = useMemo(() => buildConditionSummary(summary), [summary]);
  const interaction = useMemo(() => buildInteractionSummary(summary), [summary]);
  const dataQuality = useMemo(() => buildDataQuality(summary), [summary]);

  const totals = useMemo(() => {
    const assigned = conditionRows.reduce((a, r) => a + r.assigned, 0);
    const started = conditionRows.reduce((a, r) => a + r.started, 0);
    const completed = conditionRows.reduce((a, r) => a + r.completed, 0);
    const events = (summary?.activityStats || []).reduce((a, r) => a + (Number(r.events) || 0), 0);
    return { assigned, started, completed, events };
  }, [conditionRows, summary]);

  const exportEverything = () => downloadJson(`xai-study-export-${nowStamp()}.json`, summary);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Top stats */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <StatCard label="Assigned" value={totals.assigned} sub="counter-balanced participants" />
        <StatCard label="Started" value={totals.started} sub="submitted the starting form" accent={C.blue} />
        <StatCard
          label="Completed"
          value={totals.completed}
          sub={totals.started ? `${Math.round((totals.completed / totals.started) * 100)}% of started` : "reached the end"}
          accent={C.green}
        />
        <StatCard label="Interaction events" value={totals.events.toLocaleString()} sub="hovers, clicks & pins logged" accent={C.amber} />
      </div>

      {/* Per-condition composites */}
      <SectionCard
        title="Per-condition results"
        subtitle="Completion and mean questionnaire composites by explanation condition. Bars are normalized to each scale."
        right={<ExportButton onClick={() => downloadCsv(`condition-summary-${nowStamp()}.csv`, conditionRows)}>Condition summary CSV</ExportButton>}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
            <thead>
              <tr>
                <th style={th()}>Condition</th>
                <th style={th({ textAlign: "right" })}>Assigned</th>
                <th style={th({ textAlign: "right" })}>Started</th>
                <th style={th({ textAlign: "right" })}>Completed</th>
                <th style={th({ minWidth: 160 })}>Caption quality (1–5)</th>
                <th style={th({ minWidth: 160 })}>Trust (1–7)</th>
                <th style={th({ minWidth: 160 })}>SUS (0–100)</th>
              </tr>
            </thead>
            <tbody>
              {conditionRows.map((r) => (
                <tr key={r.mode}>
                  <td style={td({ fontWeight: 600, color: C.ink })}>{r.label}</td>
                  <td style={td({ textAlign: "right", fontVariantNumeric: "tabular-nums" })}>{r.assigned}</td>
                  <td style={td({ textAlign: "right", fontVariantNumeric: "tabular-nums" })}>{r.started}</td>
                  <td style={td({ textAlign: "right", fontVariantNumeric: "tabular-nums" })}>{r.completed}</td>
                  <td style={td()}>
                    <Bar value={r.meanCaptionQuality} max={5} color={C.blue} text={fmt(r.meanCaptionQuality)} />
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>n={r.captionRatingCount}</div>
                  </td>
                  <td style={td()}>
                    <Bar value={r.meanTrust} max={7} color={C.green} text={fmt(r.meanTrust)} />
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>n={r.trustCount}</div>
                  </td>
                  <td style={td()}>
                    <Bar value={r.meanSus} max={100} color={C.amber} text={fmt(r.meanSus)} />
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>n={r.susCount}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 12, marginBottom: 0 }}>
          Trust = mean of the 11 core Jian-scale items (negative items reverse-scored). The 12th item, `integrity`,
          is stored from migration 0007 onward but excluded here for comparability with earlier participants. Trust
          pools all post-phase measurements.
        </p>
      </SectionCard>

      {/* Interaction breakdown */}
      <SectionCard
        title="Interaction with the explanation & image"
        subtitle="Logged events and hover/pin dwell time by condition and target. NO EXPLANATION has no interactive targets by design."
        right={
          <ExportButton onClick={() => downloadCsv(`interaction-stats-${nowStamp()}.csv`, summary?.activityStats || [])}>
            Interaction stats CSV
          </ExportButton>
        }
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
            <thead>
              <tr>
                <th style={th()}>Condition</th>
                <th style={th({ textAlign: "right" })}>Total events</th>
                <th style={th({ textAlign: "right" })}>Total dwell</th>
                {TARGET_TYPES.map((t) => (
                  <th key={t} style={th({ textAlign: "right" })}>
                    {TARGET_LABEL[t]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CONDITIONS.map((mode) => {
                const m = interaction[mode];
                return (
                  <tr key={mode}>
                    <td style={td({ fontWeight: 600, color: C.ink })}>{conditionLabel(mode)}</td>
                    <td style={td({ textAlign: "right", fontVariantNumeric: "tabular-nums" })}>
                      {(m?.total || 0).toLocaleString()}
                    </td>
                    <td style={td({ textAlign: "right", fontVariantNumeric: "tabular-nums" })}>{ms(m?.totalDwellMs || 0)}</td>
                    {TARGET_TYPES.map((t) => (
                      <td key={t} style={td({ textAlign: "right", fontVariantNumeric: "tabular-nums" })}>
                        {m?.byTarget?.[t]?.events ? m.byTarget[t].events.toLocaleString() : "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Collection control: top-up queue + exclusions */}
      {((summary?.queue || []).length > 0 || (summary?.exclusions || []).length > 0) && (
        <SectionCard
          title="Collection control"
          subtitle="Queued conditions are handed out before adaptive balancing resumes. Excluded participants stop counting toward their condition's load."
        >
          {(summary?.queue || []).length > 0 && (
            <>
              <h3 style={{ marginBottom: 8 }}>
                Top-up queue{" "}
                <span style={{ color: C.muted, fontWeight: 400 }}>
                  ({summary.queue.filter((q) => q.status === "completed").length} completed ·{" "}
                  {summary.queue.filter((q) => q.status === "in_progress").length} in progress ·{" "}
                  {summary.queue.filter((q) => q.status === "open").length} still open)
                </span>
              </h3>
              <div className="tbl-wrap" style={{ overflowX: "auto", border: `1px solid ${C.line2}`, borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                  <thead>
                    <tr>
                      {["#", "Condition", "Status", "Claimed by", "Note"].map((h) => (
                        <th key={h} style={th()}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summary.queue.map((q) => (
                      <tr key={q.id}>
                        <td style={td()}>{q.id}</td>
                        <td style={td({ fontWeight: 600, color: C.ink })}>{conditionLabel(q.assigned_mode)}</td>
                        <td style={td()}>
                          {q.status === "completed" ? <Badge color={C.green}>completed</Badge>
                            : q.status === "in_progress" ? <Badge color={C.amber}>in progress</Badge>
                            : <Badge color={C.blue}>open</Badge>}
                        </td>
                        <td style={td({ fontFamily: "ui-monospace, monospace", fontSize: 12 })}>
                          {q.claimed_by ? String(q.claimed_by).slice(0, 12) : "—"}
                        </td>
                        <td style={td({ whiteSpace: "normal" })}>{q.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(summary?.exclusions || []).length > 0 && (
            <>
              <h3 style={{ margin: "18px 0 8px" }}>
                Excluded participants{" "}
                <span style={{ color: C.muted, fontWeight: 400 }}>({summary.exclusions.length})</span>
              </h3>
              <div className="tbl-wrap" style={{ overflowX: "auto", border: `1px solid ${C.line2}`, borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                  <thead>
                    <tr>{["Participant", "Reason", "By", "When"].map((h) => (
                      <th key={h} style={th()}>{h}</th>))}</tr>
                  </thead>
                  <tbody>
                    {summary.exclusions.map((x) => (
                      <tr key={x.participant_code}>
                        <td style={td({ fontFamily: "ui-monospace, monospace", fontSize: 12 })}>
                          {String(x.participant_code).slice(0, 14)}
                        </td>
                        <td style={td({ whiteSpace: "normal" })}>{x.reason}</td>
                        <td style={td()}>{x.excluded_by || "—"}</td>
                        <td style={td()}>{x.created_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: 12.5, color: C.muted, marginTop: 10, marginBottom: 0 }}>
                Exclusions are recorded in the database, so the assignment logic and every export agree on
                who counts. They are <strong>not</strong> yet filtered out of the numbers above — treat the
                condition summary as including them until the exports are re-run.
              </p>
            </>
          )}
        </SectionCard>
      )}

      {/* Data quality */}
      <SectionCard
        title="Data quality"
        subtitle="Participants whose row counts deviate from a clean run (refresh-restarts, duplicate ratings, dropouts). Caption means above already exclude duplicate ratings for the same image."
        right={
          dataQuality.length ? (
            <ExportButton onClick={() => downloadCsv(`data-quality-${nowStamp()}.csv`, dataQuality)}>
              Data-quality CSV
            </ExportButton>
          ) : null
        }
      >
        {!dataQuality.length ? (
          <p style={{ margin: 0, color: C.green, fontWeight: 600 }}>
            ✓ No anomalies — every participant has 1 starting row, 30 unique image ratings, 3 post-phase rows and an ending.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={th()}>Participant</th>
                  <th style={th()}>Condition</th>
                  <th style={th({ textAlign: "right" })}>Start</th>
                  <th style={th({ textAlign: "right" })}>Ratings</th>
                  <th style={th({ textAlign: "right" })}>Unique</th>
                  <th style={th({ textAlign: "right" })}>Phase</th>
                  <th style={th()}>Issue</th>
                </tr>
              </thead>
              <tbody>
                {dataQuality.map((d) => (
                  <tr key={d.participantCode}>
                    <td style={td({ fontFamily: "ui-monospace, monospace", fontSize: 12 })}>{d.participantCode}</td>
                    <td style={td()}>{d.label}</td>
                    <td style={td({ textAlign: "right" })}>{d.starting}</td>
                    <td style={td({ textAlign: "right" })}>{d.images}</td>
                    <td style={td({ textAlign: "right" })}>{d.distinctImages}</td>
                    <td style={td({ textAlign: "right" })}>{d.phases}</td>
                    <td style={td({ color: d.completed ? C.amber : C.red, fontWeight: 600 })}>{d.flags}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Raw exports */}
      <SectionCard title="Raw data export" subtitle="Download the underlying tables for analysis in R / Python / spreadsheets.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <ExportButton onClick={() => downloadCsv(`starting-${nowStamp()}.csv`, summary?.starting || [])}>Starting CSV</ExportButton>
          <ExportButton onClick={() => downloadCsv(`after-image-${nowStamp()}.csv`, summary?.afterImage || [])}>After-image CSV</ExportButton>
          <ExportButton onClick={() => downloadCsv(`after-phase-${nowStamp()}.csv`, summary?.afterPhase || [])}>After-phase CSV</ExportButton>
          <ExportButton onClick={() => downloadCsv(`ending-${nowStamp()}.csv`, summary?.ending || [])}>Ending CSV</ExportButton>
          <ExportButton onClick={() => downloadCsv(`assignments-${nowStamp()}.csv`, summary?.assignments || [])}>Assignments CSV</ExportButton>
          <ExportButton onClick={exportEverything}>Everything (JSON)</ExportButton>
        </div>
      </SectionCard>
    </div>
  );
}

// ---------- Participants tab ----------

function ParticipantsTab({ participants, adminToken, error, setError }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter(
      (p) =>
        String(p.participantCode).toLowerCase().includes(q) ||
        String(p.assignedMode || "").toLowerCase().includes(q)
    );
  }, [participants, query]);

  const loadDetail = async (participant) => {
    setLoadingDetail(true);
    setError("");
    setSelected(participant);
    setDetail(null);
    setActivity(null);
    try {
      const [q, a] = await Promise.all([
        getAdminQuestionnaires(participant.assignedMode, participant.participantCode, adminToken),
        getAdminActivity(participant.assignedMode, participant.participantCode, adminToken).catch(() => null),
      ]);
      setDetail(q);
      setActivity(a?.activity || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load participant data.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const files = (p) => new Set(p.availableFiles || []);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 400px) minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
      <aside style={{ background: C.panel, borderRadius: 16, border: `1px solid ${C.line}`, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 style={{ margin: 0, color: C.ink, fontSize: 16 }}>Participants</h2>
          <span style={{ color: C.muted, fontSize: 13 }}>{filtered.length}</span>
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by code or condition…"
          style={{ width: "100%", marginTop: 10, padding: "9px 11px", borderRadius: 10, border: `1px solid ${C.line2}`, fontSize: 14, boxSizing: "border-box" }}
        />
        {!participants.length ? (
          <p style={{ margin: "12px 0 0", color: C.muted }}>Load data to inspect participants.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "68vh", overflow: "auto", marginTop: 12 }}>
            {filtered.map((p) => {
              const isSel =
                selected?.participantCode === p.participantCode && selected?.assignedMode === p.assignedMode;
              const f = files(p);
              const done = f.has("ending");
              return (
                <button
                  key={`${p.assignedMode}-${p.participantCode}`}
                  type="button"
                  onClick={() => loadDetail(p)}
                  style={{
                    textAlign: "left",
                    padding: 12,
                    borderRadius: 12,
                    border: isSel ? `2px solid ${C.blue}` : `1px solid ${C.line}`,
                    background: isSel ? C.blueBg : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ color: C.ink, fontSize: 14 }}>{p.participantCode}</strong>
                    {done ? <Badge color={C.green}>done</Badge> : <Badge color={C.amber}>partial</Badge>}
                  </div>
                  <div style={{ color: C.sub, marginTop: 4, fontSize: 13 }}>{conditionLabel(p.assignedMode)}</div>
                  <div style={{ marginTop: 6 }}>
                    <Badge color={f.has("starting") ? C.blue : C.line2}>start</Badge>
                    <Badge color={f.has("afterImage") ? C.blue : C.line2}>image</Badge>
                    <Badge color={f.has("afterPhase") ? C.blue : C.line2}>phase</Badge>
                    <Badge color={f.has("ending") ? C.blue : C.line2}>end</Badge>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      <main style={{ background: C.panel, borderRadius: 16, border: `1px solid ${C.line}`, padding: 20 }}>
        {!selected ? (
          <p style={{ margin: 0, color: C.muted }}>Select a participant to inspect their submitted data and interaction log.</p>
        ) : loadingDetail ? (
          <p style={{ margin: 0, color: C.sub }}>Loading participant data…</p>
        ) : detail ? (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <p style={{ color: C.sub, margin: 0 }}>
                <strong style={{ color: C.ink }}>{detail.participantCode}</strong> · {conditionLabel(detail.assignedMode)}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <ExportButton
                  onClick={() =>
                    downloadJson(`participant-${detail.participantCode}-${nowStamp()}.json`, { ...detail, activity })
                  }
                >
                  This participant (JSON)
                </ExportButton>
                <ExportButton onClick={() => downloadCsv(`activity-${detail.participantCode}-${nowStamp()}.csv`, activity || [])}>
                  Activity CSV
                </ExportButton>
              </div>
            </div>
            {error && <p style={{ color: C.red }}>{error}</p>}
            <div style={{ marginTop: 16 }}>
              <DataTable title="Starting questionnaire" rows={detail.questionnaires?.starting || []} />
              <DataTable title="After image questionnaire" rows={detail.questionnaires?.afterImage || []} />
              <DataTable title="After phase questionnaire" rows={detail.questionnaires?.afterPhase || []} />
              <DataTable title="Ending questionnaire" rows={detail.questionnaires?.ending || []} />
              <DataTable title="Interaction log (activity_logs)" rows={activity || []} maxHeight={420} />
            </div>
          </>
        ) : (
          <p style={{ margin: 0, color: C.muted }}>No data loaded.</p>
        )}
      </main>
    </div>
  );
}

// ---------- Root ----------

function AdminView() {
  const [adminToken, setAdminToken] = useState("");
  const [participants, setParticipants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [prolificOnly, setProlificOnly] = useState(true);

  const load = async (prolificFlag = prolificOnly) => {
    setLoading(true);
    setError("");
    try {
      const token = adminToken.trim();
      const [p, s] = await Promise.all([
        getAdminParticipants(token),
        getAdminSummary(token, prolificFlag),
      ]);
      setParticipants(p.participants || []);
      setSummary(s);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load admin data. Check the token.");
      setParticipants([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Toggling the Prolific filter re-queries the summary (its interaction stats
  // are aggregated server-side, so they must be re-filtered there).
  const changeProlificOnly = async (next) => {
    setProlificOnly(next);
    if (!summary) return;
    setLoading(true);
    setError("");
    try {
      const s = await getAdminSummary(adminToken.trim(), next);
      setSummary(s);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to refresh with the Prolific filter.");
    } finally {
      setLoading(false);
    }
  };

  const loaded = !!summary;
  const visibleParticipants = prolificOnly
    ? participants.filter((p) => isProlificCode(p.participantCode))
    : participants;

  const tabBtn = (id, text) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      style={{
        padding: "9px 16px",
        borderRadius: 10,
        border: `1px solid ${tab === id ? C.blue : C.line2}`,
        background: tab === id ? C.blue : "#fff",
        color: tab === id ? "#fff" : C.blueInk,
        fontWeight: 600,
        fontSize: 14,
        cursor: "pointer",
      }}
    >
      {text}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: 24 }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <header>
          <h1 style={{ margin: 0, color: C.ink }}>Photobook Admin & Analysis</h1>
          <p style={{ marginTop: 6, color: C.sub }}>
            Live study data: completion, per-condition results, interaction metrics, and full data export.
          </p>
        </header>

        <section style={{ background: C.panel, borderRadius: 16, border: `1px solid ${C.line}`, padding: 18 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
            <label style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", gap: 8, color: "#1e293b", fontWeight: 600 }}>
              Admin token
              <input
                type="password"
                value={adminToken}
                onChange={(event) => setAdminToken(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && adminToken.trim() && !loading) load();
                }}
                placeholder="Enter shared admin token"
                style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.line2}`, fontSize: 15 }}
              />
            </label>
            <Button variant="contained" onClick={load} disabled={!adminToken.trim() || loading}>
              {loading ? "Loading…" : loaded ? "Refresh" : "Load data"}
            </Button>
          </div>
          {error && <p style={{ marginTop: 12, color: C.red }}>{error}</p>}
        </section>

        {loaded && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {tabBtn("overview", "Overview & export")}
            {tabBtn("participants", "Participants")}
            <label
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: C.ink,
                cursor: "pointer",
                background: prolificOnly ? C.blueBg : "#fff",
                border: `1px solid ${prolificOnly ? C.blue : C.line2}`,
                borderRadius: 10,
                padding: "8px 12px",
              }}
            >
              <input
                type="checkbox"
                checked={prolificOnly}
                onChange={(e) => changeProlificOnly(e.target.checked)}
              />
              Prolific participants only
              <span style={{ color: C.muted, fontWeight: 400 }}>
                ({prolificOnly ? "excludes pilots/tests" : "showing everything"})
              </span>
            </label>
          </div>
        )}

        {!loaded ? (
          <p style={{ color: C.muted }}>Enter the admin token and load the data to see the analysis.</p>
        ) : tab === "overview" ? (
          <OverviewTab summary={summary} />
        ) : (
          <ParticipantsTab
            participants={visibleParticipants}
            adminToken={adminToken.trim()}
            error={error}
            setError={setError}
          />
        )}
      </div>
    </div>
  );
}

export default AdminView;
